import { SystemOverviewMetricType } from "$/__generated__/graphql";
import {
  twoDecimalPlaceRounding,
  threeDecimalPlaceRounding,
  coerceNumberFormat,
  falsyToNAFormatter,
  twoDecimalPlaceRoundingCellFormatter,
  threeDecimalPlaceRoundingCellFormatter,
  scienceNotationRoundingCellFormatter,
  yearRoundingCellFormatter,
  scienceNotationRounding,
} from "components/tables/utils/formatters";
import type {
  SummariesFormatters,
  SummariesFormattersGrouped,
} from "hooks/queries/types";

import type {
  KeyConsequenceFactorsColumnDef,
  KeyLikelihoodFactorsColumnDef,
  RiskManagementTableColumnDef,
  RiskSummariesColumnDef,
  WellAttributesColumnDef,
} from "./types";

export const WELL_ATTRIBUTES_BASE_DEFS = [
  {
    id: "BasicAttributes",
    header: "Basic Attributes",
    columns: [
      {
        id: "Name",
        header: "Well Name",
        accessorKey: "name",
        enablePinning: true,
        enableSorting: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "FieldCode",
        header: "Field",
        accessorKey: "fieldCode",
        enablePinning: true,
        enableSorting: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "FieldName",
        header: "Field Name",
        accessorKey: "fieldName",
        enablePinning: true,
        enableSorting: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "WellType",
        header: "Well Type",
        accessorKey: "wellType",
        enablePinning: true,
        enableSorting: true,
        meta: { isPinned: true },
      },
      {
        id: "DrilledYear",
        header: "Drilled Year",
        accessorKey: "drilledYear",
        enablePinning: true,
        enableSorting: true,
        meta: { isPinned: true },
        cell: yearRoundingCellFormatter,
      },
    ],
  },
  {
    id: "WellAttributes",
    header: "Well Attributes",
    columns: [
      {
        id: "WellConfiguration",
        header: "Well Configuration",
        columns: [
          {
            id: "SurfaceCasing",
            header: "Surface Casing",
            accessorKey: "surfaceCasing",
            cell: threeDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "IntermediateCasingThree",
            header: "Intermediate Casing 3",
            accessorKey: "intermediateCasingThree",
            cell: threeDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "IntermediateCasingTwo",
            header: "Intermediate Casing 2",
            accessorKey: "intermediateCasingTwo",
            cell: threeDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "IntermediateCasingOne",
            header: "Intermediate Casing 1",
            accessorKey: "intermediateCasingOne",
            cell: threeDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "ProductionCasing",
            header: "Production Casing",
            accessorKey: "productionCasing",
            cell: threeDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "Tubing",
            header: "Tubing",
            accessorKey: "tubing",
            cell: threeDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "PackerAboveTubing",
            header: "Packer Above Tubing",
            accessorKey: "packerAboveTubing",
            maxSize: 80,
          },
          {
            id: "NeedsAnnualPressureMonitoringPort",
            header: "Needs Annular Pressure Monitoring Port",
            accessorKey: "needsAnnualPressureMonitoringPort",
            maxSize: 80,
          },
        ],
      },
      {
        id: "CasingDepths",
        header: "Casing Depths",
        columns: [
          {
            id: "SurfaceCasingDepth",
            header: "Surface Casing Depth",
            accessorKey: "surfaceCasingDepth",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "IntermediateCasingDepth",
            header: "Intermediate Casing Depth",
            accessorKey: "intermediateCasingDepth",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "ProductionCasingDepth",
            header: "Production Casing Depth",
            accessorKey: "productionCasingDepth",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
    ],
  },
] satisfies WellAttributesColumnDef;

const modeWellAttributes: SummariesFormatters = {
  intermediateCasingDepth: coerceNumberFormat(twoDecimalPlaceRounding),
  surfaceCasingDepth: coerceNumberFormat(twoDecimalPlaceRounding),
  productionCasingDepth: coerceNumberFormat(twoDecimalPlaceRounding),
};

const medianWellAttributes: SummariesFormatters = {
  surfaceCasing: coerceNumberFormat(threeDecimalPlaceRounding),
  intermediateCasingThree: coerceNumberFormat(threeDecimalPlaceRounding),
  intermediateCasingTwo: coerceNumberFormat(threeDecimalPlaceRounding),
  intermediateCasingOne: coerceNumberFormat(threeDecimalPlaceRounding),
  productionCasing: coerceNumberFormat(threeDecimalPlaceRounding),
  tubing: coerceNumberFormat(threeDecimalPlaceRounding),
  packerAboveTubing: falsyToNAFormatter,
  needsAnnualPressureMonitoringPort: falsyToNAFormatter,
};

export const WELL_ATTRIBUTES_ROUNDING: SummariesFormattersGrouped = {
  [SystemOverviewMetricType.Mode]: modeWellAttributes,
  [SystemOverviewMetricType.Median]: medianWellAttributes,
  [SystemOverviewMetricType.Total]: {
    ...medianWellAttributes,
    surfaceCasingDepth: coerceNumberFormat(twoDecimalPlaceRounding),
    intermediateCasingDepth: coerceNumberFormat(twoDecimalPlaceRounding),
    productionCasingDepth: coerceNumberFormat(twoDecimalPlaceRounding),
  },
  surfaceCasing: threeDecimalPlaceRounding,
  intermediateCasingThree: threeDecimalPlaceRounding,
  intermediateCasingTwo: threeDecimalPlaceRounding,
  intermediateCasingOne: threeDecimalPlaceRounding,
  productionCasing: threeDecimalPlaceRounding,
  tubing: threeDecimalPlaceRounding,
  surfaceCasingDepth: twoDecimalPlaceRounding,
  intermediateCasingDepth: twoDecimalPlaceRounding,
  productionCasingDepth: twoDecimalPlaceRounding,
};

export const RISK_SUMMARY_BASE_DEFS = [
  {
    id: "BasicAttributes",
    header: "Basic Attributes",
    columns: [
      {
        id: "Name",
        header: "Well Name",
        accessorKey: "name",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "FieldCode",
        header: "Field",
        accessorKey: "fieldCode",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "FieldName",
        header: "Field Name",
        accessorKey: "fieldName",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "WellType",
        header: "Well Type",
        accessorKey: "wellType",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "DrilledYear",
        header: "Drilled Year",
        accessorKey: "drilledYear",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
        cell: yearRoundingCellFormatter,
      },
    ],
  },
  {
    id: "Risk",
    header: "Risk",
    columns: [
      {
        id: "OperationalRisk",
        header: "Operational Risk",
        columns: [
          {
            id: "OperationalTotalRisk",
            header: "Total Risk",
            accessorKey: "baselineOperationalTotalRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "OperationalSurfaceRisk",
            header: "Surface Risk",
            accessorKey: "baselineOperationalSurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "OperationalSubsurfaceRisk",
            header: "Subsurface Risk",
            accessorKey: "baselineOperationalSubsurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "OperationalNuisanceRisk",
            header: "Nuisance Risk",
            accessorKey: "baselineOperationalNuisanceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
      {
        id: "InterventionRisk",
        header: "Intervention Risk",
        columns: [
          {
            id: "InterventionTotalRisk",
            header: "Total Risk",
            accessorKey: "interventionTotalRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "InterventionWirelineRisk",
            header: "Wireline Risk",
            accessorKey: "interventionWirelineRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "InterventionCoiledTubingRisk",
            header: "Coiled Tubing Risk",
            accessorKey: "interventionCoiledTubingRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "InterventionSnubbingRisk",
            header: "Snubbing Risk",
            accessorKey: "interventionSnubbingRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "InterventionWorkoverRisk",
            header: "Workover Risk",
            accessorKey: "interventionWorkoverRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
      {
        id: "SingleInterventionRisk",
        header: "Single Intervention Risk",
        columns: [
          {
            id: "SingleInterventionWirelineRisk",
            header: "Wireline Risk",
            accessorKey: "singleInterventionWirelineRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "SingleInterventionCoiledTubingRisk",
            header: "Coiled Tubing Risk",
            accessorKey: "singleInterventionCoiledTubingRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "SingleInterventionSnubbingRisk",
            header: "Snubbing Risk",
            accessorKey: "singleInterventionSnubbingRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "SingleInterventionWorkoverRisk",
            header: "Workover Risk",
            accessorKey: "singleInterventionWorkoverRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
      {
        id: "PostMitigationRiskTubingAndPackerInstallation",
        header: "Post-Mitigation Risk: Tubing and Packer Installation",
        columns: [
          {
            id: "TubingPackerInstallationTotalRisk",
            header: "Total Risk",
            accessorKey: "tubingPackerInstallationTotalRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "TubingPackerInstallationSurfaceRisk",
            header: "Surface Risk",
            accessorKey: "tubingPackerInstallationSurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "TubingPackerInstallationSubsurfaceRisk",
            header: "Subsurface Risk",
            accessorKey: "tubingPackerInstallationSubsurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "TubingPackerInstallationNuisanceRisk",
            header: "Nuisance Risk",
            accessorKey: "tubingPackerInstallationNuisanceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
      {
        id: "PostMitigationRiskCorrosionLogging",
        header: "Post-Mitigation Risk: Corrosion Logging",
        columns: [
          {
            id: "CorrosionLoggingTotalRisk",
            header: "Total Risk",
            accessorKey: "corrosionLoggingTotalRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "CorrosionLoggingSurfaceRisk",
            header: "Surface Risk",
            accessorKey: "corrosionLoggingSurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "CorrosionLoggingSubsurfaceRisk",
            header: "Subsurface Risk",
            accessorKey: "corrosionLoggingSubsurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "CorrosionLoggingNuisanceRisk",
            header: "Nuisance Risk",
            accessorKey: "corrosionLoggingNuisanceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
      {
        id: "PostMitigationRiskCorrosionLoggingAndRepair",
        header: "Post-Mitigation Risk: Corrosion Logging and Repair",
        columns: [
          {
            id: "CorrosionLoggingRepairTotalRisk",
            header: "Total Risk",
            accessorKey: "corrosionLoggingRepairTotalRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "CorrosionLoggingRepairSurfaceRisk",
            header: "Surface Risk",
            accessorKey: "corrosionLoggingRepairSurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "CorrosionLoggingRepairSubsurfaceRisk",
            header: "Subsurface Risk",
            accessorKey: "corrosionLoggingRepairSubsurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "CorrosionLoggingRepairNuisanceRisk",
            header: "Nuisance Risk",
            accessorKey: "corrosionLoggingRepairNuisanceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
      {
        id: "PostMitigationRisksssVInstallation",
        header: "Post-Mitigation Risk: SSSV Installation",
        columns: [
          {
            id: "SssvInstallationTotalRisk",
            header: "Total Risk",
            accessorKey: "sssvInstallationTotalRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "SssvInstallationSurfaceRisk",
            header: "Surface Risk",
            accessorKey: "sssvInstallationSurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "SssvInstallationSubsurfaceRisk",
            header: "Subsurface Risk",
            accessorKey: "sssvInstallationSubsurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "SssvInstallationNuisanceRisk",
            header: "Nuisance Risk",
            accessorKey: "sssvInstallationNuisanceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
      {
        id: "PostMitigationRiskPluggingAndAbandonment",
        header: "Post-Mitigation Risk: Plugging and Abandonment",
        columns: [
          {
            id: "PluggingAbandonmentTotalRisk",
            header: "Total Risk",
            accessorKey: "pluggingAbandonmentTotalRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "PluggingAbandonmentSurfaceRisk",
            header: "Surface",
            accessorKey: "pluggingAbandonmentSurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "PluggingAbandonmentSubsurfaceRisk",
            header: "Subsurface Risk",
            accessorKey: "pluggingAbandonmentSubsurfaceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "PluggingAbandonmentNuisanceRisk",
            header: "Nuisance Risk",
            accessorKey: "pluggingAbandonmentNuisanceRisk",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
    ],
  },
] satisfies RiskSummariesColumnDef;

export const RISK_SUMMARY_ROUNDING: SummariesFormattersGrouped = {
  [SystemOverviewMetricType.Mode]: {
    baselineOperationalTotalRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    baselineOperationalSurfaceRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    baselineOperationalSubsurfaceRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    baselineOperationalNuisanceRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    interventionTotalRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    interventionWirelineRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    interventionCoiledTubingRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    interventionSnubbingRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    interventionWorkoverRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    singleInterventionWirelineRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    singleInterventionCoiledTubingRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    singleInterventionSnubbingRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    singleInterventionWorkoverRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    tubingPackerInstallationTotalRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    tubingPackerInstallationSurfaceRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    tubingPackerInstallationSubsurfaceRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    tubingPackerInstallationNuisanceRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    corrosionLoggingTotalRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    corrosionLoggingSurfaceRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    corrosionLoggingSubsurfaceRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    corrosionLoggingNuisanceRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    corrosionLoggingRepairTotalRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    corrosionLoggingRepairSurfaceRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    corrosionLoggingRepairSubsurfaceRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    corrosionLoggingRepairNuisanceRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    sssvInstallationTotalRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    sssvInstallationSurfaceRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    sssvInstallationSubsurfaceRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    sssvInstallationNuisanceRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    pluggingAbandonmentTotalRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    pluggingAbandonmentSurfaceRisk: coerceNumberFormat(twoDecimalPlaceRounding),
    pluggingAbandonmentSubsurfaceRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
    pluggingAbandonmentNuisanceRisk: coerceNumberFormat(
      twoDecimalPlaceRounding,
    ),
  },
  baselineOperationalTotalRisk: twoDecimalPlaceRounding,
  baselineOperationalSurfaceRisk: twoDecimalPlaceRounding,
  baselineOperationalSubsurfaceRisk: twoDecimalPlaceRounding,
  baselineOperationalNuisanceRisk: twoDecimalPlaceRounding,
  interventionTotalRisk: twoDecimalPlaceRounding,
  interventionWirelineRisk: twoDecimalPlaceRounding,
  interventionCoiledTubingRisk: twoDecimalPlaceRounding,
  interventionSnubbingRisk: twoDecimalPlaceRounding,
  interventionWorkoverRisk: twoDecimalPlaceRounding,
  singleInterventionWirelineRisk: twoDecimalPlaceRounding,
  singleInterventionCoiledTubingRisk: twoDecimalPlaceRounding,
  singleInterventionSnubbingRisk: twoDecimalPlaceRounding,
  singleInterventionWorkoverRisk: twoDecimalPlaceRounding,
  tubingPackerInstallationTotalRisk: twoDecimalPlaceRounding,
  tubingPackerInstallationSurfaceRisk: twoDecimalPlaceRounding,
  tubingPackerInstallationSubsurfaceRisk: twoDecimalPlaceRounding,
  tubingPackerInstallationNuisanceRisk: twoDecimalPlaceRounding,
  corrosionLoggingTotalRisk: twoDecimalPlaceRounding,
  corrosionLoggingSurfaceRisk: twoDecimalPlaceRounding,
  corrosionLoggingSubsurfaceRisk: twoDecimalPlaceRounding,
  corrosionLoggingNuisanceRisk: twoDecimalPlaceRounding,
  corrosionLoggingRepairTotalRisk: twoDecimalPlaceRounding,
  corrosionLoggingRepairSurfaceRisk: twoDecimalPlaceRounding,
  corrosionLoggingRepairSubsurfaceRisk: twoDecimalPlaceRounding,
  corrosionLoggingRepairNuisanceRisk: twoDecimalPlaceRounding,
  sssvInstallationTotalRisk: twoDecimalPlaceRounding,
  sssvInstallationSurfaceRisk: twoDecimalPlaceRounding,
  sssvInstallationSubsurfaceRisk: twoDecimalPlaceRounding,
  sssvInstallationNuisanceRisk: twoDecimalPlaceRounding,
  pluggingAbandonmentTotalRisk: twoDecimalPlaceRounding,
  pluggingAbandonmentSurfaceRisk: twoDecimalPlaceRounding,
  pluggingAbandonmentSubsurfaceRisk: twoDecimalPlaceRounding,
  pluggingAbandonmentNuisanceRisk: twoDecimalPlaceRounding,
};

export const KEY_LIKELIHOOD_FACTORS_BASE_DEFS = [
  {
    id: "BasicAttributes",
    header: "Basic Attributes",
    columns: [
      {
        id: "Name",
        header: "Well Name",
        accessorKey: "name",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "FieldCode",
        header: "Field",
        accessorKey: "fieldCode",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "FieldName",
        header: "Field Name",
        accessorKey: "fieldName",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "WellType",
        header: "Well Type",
        accessorKey: "wellType",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "DrilledYear",
        header: "Drilled Year",
        accessorKey: "drilledYear",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
        cell: yearRoundingCellFormatter,
      },
      {
        id: "TotalOperationalRisk",
        header: "Total Operational Risk",
        accessorKey: "totalOperationalRisk",
        cell: twoDecimalPlaceRoundingCellFormatter,
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "TotalInterventionRisk",
        header: "Total Intervention Risk",
        accessorKey: "totalInterventionRisk",
        cell: twoDecimalPlaceRoundingCellFormatter,
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
    ],
  },
  {
    id: "KeyLikelihoodFactors",
    header: "Key Likelihood Factors",
    columns: [
      {
        id: "AnnularPressureReadings",
        header: "Annular Pressure Readings [psi]",
        columns: [
          {
            id: "PressureTPC",
            header: "Pressure T_PC",
            accessorKey: "pressureTpc",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "PressureIC1SC",
            header: "Pressure IC1_SC",
            accessorKey: "pressureIc1sc",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "PressurePCIC1",
            header: "Pressure PC_IC1",
            accessorKey: "pressurePcic1",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "PressurePCSC",
            header: "Pressure PC_SC",
            accessorKey: "pressurePcsc",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
      {
        id: "SurfaceAndEnvironmentalFactors",
        header: "Surface and Environmental Factors",
        columns: [
          {
            id: "NumberOfChrismasTreeValves",
            header: "Number of XT Valves",
            accessorKey: "numberOfChrismasTreeValves",
          },
          {
            id: "SecurityFencing",
            header: "Security Fencing",
            accessorKey: "securityFencing",
          },
          {
            id: "FarmImplementsUse",
            header: "Farm Implements Use",
            accessorKey: "farmImplementsUse",
          },
          {
            id: "ATVOrSnowmobileUse",
            header: "ATV or Snowmobile Use",
            accessorKey: "atvOrSnowmobileUse",
          },
          {
            id: "PublicTransitRoadwaysNearBy",
            header: "Public Transit Roadways Nearby",
            accessorKey: "publicTransitRoadwaysNearBy",
          },
          {
            id: "HuntingUse",
            header: "Hunting Use",
            accessorKey: "huntingUse",
          },
          {
            id: "RecreationalUseOfArea",
            header: "Recreational Use of Area",
            accessorKey: "recreationalUseOfArea",
          },
          {
            id: "TargetOfMaliciousIntent",
            header: "Target of Malicious Intent",
            accessorKey: "targetOfMaliciousIntent",
          },
          {
            id: "DistanceToNearbyTree",
            header: "Distance to Nearby Tree",
            accessorKey: "distanceToNearbyTree",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "AreaProneToFlooding",
            header: "Area Prone to Flooding",
            accessorKey: "areaProneToFlooding",
          },
        ],
      },
      {
        id: "DownholeCorrosionFactors",
        header: "Downhole Corrosion Factors",
        columns: [
          {
            id: "DepthOfDeepestCorrosionFeature",
            header: "Depth of Deepest Corrosion Feature",
            accessorKey: "depthOfDeepestCorrosionFeature",
          },
          {
            id: "FeatureCountClassTwo",
            header: "Feature Count Class 2",
            accessorKey: "featureCountClassTwo",
          },
          {
            id: "FeatureCountClassThree",
            header: "Feature Count Class 3",
            accessorKey: "featureCountClassThree",
          },
          {
            id: "FeatureCountClassFour",
            header: "Feature Count Class 4",
            accessorKey: "featureCountClassFour",
          },
          {
            id: "ClassTwoDeepestCorrosionFeature",
            header: "Class of Deepest Corrosion Feature Class 2 (flag)",
            accessorKey: "classTwoDeepestCorrosionFeature",
          },
          {
            id: "ClassThreeDeepestCorrosionFeature",
            header: "Class of Deepest Corrosion Feature Class 3 (flag)",
            accessorKey: "classThreeDeepestCorrosionFeature",
          },
          {
            id: "ClassFourDeepestCorrosionFeature",
            header: "Class of Deepest Corrosion Feature Class 4 (flag)",
            accessorKey: "classFourDeepestCorrosionFeature",
          },
          {
            id: "YearsFromLastCorrosionRepair",
            header: "Years from Last Corrosion Repair",
            accessorKey: "yearsFromLastCorrosionRepair",
          },
        ],
      },
      {
        id: "Logging",
        header: "Logging [0 = no record]",
        columns: [
          {
            id: "MostRecentGRNLog",
            header: "Most Recent GRN Log",
            accessorKey: "mostRecentGrnlog",
          },
          {
            id: "MostRecentCementBondLog",
            header: "Most Recent Cement Bond Log",
            accessorKey: "mostRecentCementBondLog",
          },
          {
            id: "MostRecentCorrosionLog",
            header: "Most Recent Corrosion Log",
            accessorKey: "mostRecentCorrosionLog",
          },
        ],
      },
    ],
  },
] satisfies KeyLikelihoodFactorsColumnDef;

const klfModeTotalFormatters = {
  pressureTpc: coerceNumberFormat(twoDecimalPlaceRounding),
  pressureIc1sc: coerceNumberFormat(twoDecimalPlaceRounding),
  pressurePcic1: coerceNumberFormat(twoDecimalPlaceRounding),
  pressurePcsc: coerceNumberFormat(twoDecimalPlaceRounding),
  distanceToNearbyTree: coerceNumberFormat(twoDecimalPlaceRounding),
  depthOfDeepestCorrosionFeature: coerceNumberFormat(twoDecimalPlaceRounding),
  yearsFromLastCorrosionRepair: coerceNumberFormat(),
  mostRecentGrnlog: coerceNumberFormat(),
  mostRecentCementBondLog: coerceNumberFormat(),
  mostRecentCorrosionLog: coerceNumberFormat(),
};

const klfMedianTotalFormatters = {
  securityFencing: falsyToNAFormatter,
  farmImplementsUse: falsyToNAFormatter,
  atvOrSnowmobileUse: falsyToNAFormatter,
  publicTransitRoadwaysNearBy: falsyToNAFormatter,
  huntingUse: falsyToNAFormatter,
  recreationalUseOfArea: falsyToNAFormatter,
  targetOfMaliciousIntent: falsyToNAFormatter,
  areaProneToFlooding: falsyToNAFormatter,
  classTwoDeepestCorrosionFeature: coerceNumberFormat(),
  classThreeDeepestCorrosionFeature: coerceNumberFormat(),
  classFourDeepestCorrosionFeature: coerceNumberFormat(),
};

export const KEY_LIKELIHOOD_FACTORS_ROUNDING: SummariesFormattersGrouped = {
  [SystemOverviewMetricType.Mode]: klfModeTotalFormatters,
  [SystemOverviewMetricType.Median]: klfMedianTotalFormatters,
  [SystemOverviewMetricType.Total]: {
    ...klfModeTotalFormatters,
    ...klfMedianTotalFormatters,
    numberOfChrismasTreeValves: coerceNumberFormat(),
    featureCountClassTwo: coerceNumberFormat(),
    featureCountClassThree: coerceNumberFormat(),
    featureCountClassFour: coerceNumberFormat(),
  },
  pressureTpc: twoDecimalPlaceRounding,
  pressureIc1sc: twoDecimalPlaceRounding,
  pressurePcic1: twoDecimalPlaceRounding,
  pressurePcsc: twoDecimalPlaceRounding,
  distanceToNearbyTree: twoDecimalPlaceRounding,
  depthOfDeepestCorrosionFeature: twoDecimalPlaceRounding,
};

export const KEY_CONSEQUENCE_FACTORS_BASE_DEFS = [
  {
    id: "BasicAttributes",
    header: "Basic Attributes",
    columns: [
      {
        id: "Name",
        header: "Well Name",
        accessorKey: "name",
        enablePinning: true,
        meta: { isPinned: true },
      },
      {
        id: "FieldCode",
        header: "Field",
        accessorKey: "fieldCode",
        enablePinning: true,
        meta: { isPinned: true },
      },
      {
        id: "FieldName",
        header: "Field Name",
        accessorKey: "fieldName",
        enablePinning: true,
        meta: { isPinned: true },
      },
      {
        id: "WellType",
        header: "Well Type",
        accessorKey: "wellType",
        enablePinning: true,
        meta: { isPinned: true },
      },
      {
        id: "DrilledYear",
        header: "Drilled Year",
        accessorKey: "drilledYear",
        enablePinning: true,
        meta: { isPinned: true },
        cell: yearRoundingCellFormatter,
      },
      {
        id: "TotalOperationalRisk",
        header: "Total Operational Risk",
        accessorKey: "totalOperationalRisk",
        cell: twoDecimalPlaceRoundingCellFormatter,
        enablePinning: true,
        meta: { isPinned: true },
      },
      {
        id: "TotalInterventionRisk",
        header: "Total Intervention Risk",
        accessorKey: "totalInterventionRisk",
        cell: twoDecimalPlaceRoundingCellFormatter,
        enablePinning: true,
        meta: { isPinned: true },
      },
    ],
  },
  {
    id: "KeyConsequenceFactors",
    header: "Key Consequence Factors",
    columns: [
      {
        id: "KeyConsequenceFactors",
        header: "Key Consequence Factors",
        columns: [
          {
            id: "AbsolututeOpenFlow",
            header: "Absolute Open Flow [Mmcf/day]",
            accessorKey: "absolututeOpenFlow",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "DwellingsWithinOneMile",
            header: "Dwellings Nearby",
            accessorKey: "dwellingsWithinOneMile",
          },
          {
            id: "BuildingsWithinThousandFeet",
            header: "Buildings Nearby",
            accessorKey: "buildingsWithinThousandFeet",
          },
          {
            id: "TotalWorkingGas",
            header: "Volumetric Flow Rate",
            accessorKey: "totalWorkingGas",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "LinerCasingDiameter",
            header: "Linear Casing Diameter",
            accessorKey: "linerCasingDiameter",
            cell: threeDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "MaxOperatingPressure",
            header: "Maximum Operating Pressure [psi]",
            accessorKey: "maxOperatingPressure",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
    ],
  },
] satisfies KeyConsequenceFactorsColumnDef;

const kcfModeTotalFormatters = {
  absolututeOpenFlow: coerceNumberFormat(twoDecimalPlaceRounding),
  dwellingsWithinOneMile: coerceNumberFormat(),
  totalWorkingGas: coerceNumberFormat(twoDecimalPlaceRounding),
  linerCasingDiameter: coerceNumberFormat(threeDecimalPlaceRounding),
  maxOperatingPressure: coerceNumberFormat(twoDecimalPlaceRounding),
};

export const KEY_CONSEQUENCE_FACTORS_ROUNDING: SummariesFormattersGrouped = {
  [SystemOverviewMetricType.Mode]: kcfModeTotalFormatters,
  [SystemOverviewMetricType.Median]: {
    buildingsWithinThousandFeet: falsyToNAFormatter,
  },
  [SystemOverviewMetricType.Total]: {
    ...kcfModeTotalFormatters,
    absolututeOpenFlow: coerceNumberFormat(twoDecimalPlaceRounding),
    dwellingsWithinOneMile: coerceNumberFormat(),
    buildingsWithinThousandFeet: falsyToNAFormatter,
  },
  absolututeOpenFlow: twoDecimalPlaceRounding,
  totalWorkingGas: twoDecimalPlaceRounding,
  linerCasingDiameter: threeDecimalPlaceRounding,
  maxOperatingPressure: twoDecimalPlaceRounding,
};

export const RISK_MANAGEMENT_TABLE_BASE_DEFS = [
  {
    id: "WellAttributes",
    header: "Well Attributes",
    columns: [
      {
        id: "Name",
        header: "Well",
        accessorKey: "name",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "FieldCode",
        header: "Field",
        accessorKey: "fieldCode",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "WellType",
        header: "Well Type",
        accessorKey: "wellType",
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
    ],
  },
  {
    id: "WellRisk",
    header: "Well Risk",
    columns: [
      {
        id: "OverallRisk",
        header: "Overall Risk",
        accessorKey: "overallRisk",
        cell: twoDecimalPlaceRoundingCellFormatter,
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "MajorEventRisk",
        header: "Major Event Risk",
        accessorKey: "majorEventRisk",
        cell: twoDecimalPlaceRoundingCellFormatter,
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
      {
        id: "ScenarioRisk",
        header: "Scenario Risk",
        accessorKey: "scenarioRisk",
        cell: twoDecimalPlaceRoundingCellFormatter,
        enablePinning: true,
        meta: {
          isPinned: true,
        },
      },
    ],
  },
  {
    id: "LikelihoodOfFailure",
    header: "Likelihood of Failure",
    columns: [
      {
        id: "LofSummary",
        header: "LoF Summary",
        columns: [
          {
            id: "LofSurface",
            header: "LoF Surface",
            accessorKey: "lofSurface",
            cell: scienceNotationRoundingCellFormatter,
          },
          {
            id: "LofSubsurface",
            header: "LoF - Subsurface",
            accessorKey: "lofSubsurface",
            cell: scienceNotationRoundingCellFormatter,
          },
          {
            id: "LofNuisance",
            header: "LoF - Nuisance (Surface)",
            accessorKey: "lofNuisance",
            cell: scienceNotationRoundingCellFormatter,
          },
          {
            id: "LofIntervention",
            header: "LoF - Intervention",
            accessorKey: "lofIntervention",
            cell: scienceNotationRoundingCellFormatter,
          },
        ],
      },
    ],
  },
  {
    id: "ConsequenceOfFailure",
    header: "Consequence of Failure",
    columns: [
      {
        id: "CofSummary",
        header: "CoF Summary",
        columns: [
          {
            id: "CofSurface",
            header: "CoF - Surface",
            accessorKey: "cofSurface",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "CofSubsurface",
            header: "CoF - Subsurface",
            accessorKey: "cofSubsurface",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "CofIntervention",
            header: "CoF - Intervention",
            accessorKey: "cofIntervention",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
      {
        id: "CofBreakdown",
        header: "CoF Breakdown",
        columns: [
          {
            id: "HealthAndSafetyTotal",
            header: "Health and Safety Total",
            accessorKey: "healthAndSafetyTotal",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "InterventionCrewOnSite",
            header: "Intervention - Crew on Site",
            accessorKey: "interventionCrewOnSite",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "InjuryFatality",
            header: "Injury/Fatality",
            accessorKey: "injuryFatality",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "Health",
            header: "Health",
            accessorKey: "health",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "DirectEconomicLossSurface",
            header: "Direct Economic Loss - Surface",
            accessorKey: "directEconomicLossSurface",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "DirectEconomicLossSubsurface",
            header: "Direct Economic Loss - Subsurface",
            accessorKey: "directEconomicLossSubsurface",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "PropertyDamage",
            header: "Property Damage",
            accessorKey: "propertyDamage",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "ResponseCost",
            header: "Response Cost",
            accessorKey: "responseCost",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "SurfaceRepairCost",
            header: "Surface Repair Cost",
            accessorKey: "surfaceRepairCost",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "DownholeRepairCost",
            header: "Downhole Repair Cost",
            accessorKey: "downholeRepairCost",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "CommodityLoss",
            header: "Commodity Loss",
            accessorKey: "commodityLoss",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "ServiceDisruption",
            header: "Service Disruption",
            accessorKey: "serviceDisruption",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "Relocation",
            header: "Relocation",
            accessorKey: "relocation",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "Regulatory",
            header: "Regulatory",
            accessorKey: "regulatory",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "EnvironmentalTotal",
            header: "Environmental Total",
            accessorKey: "environmentalTotal",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "Emissions",
            header: "Emissions",
            accessorKey: "emissions",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "AirMonitoring",
            header: "Air Monitoring",
            accessorKey: "airMonitoring",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
          {
            id: "Reputation",
            header: "Reputation",
            accessorKey: "reputation",
            cell: twoDecimalPlaceRoundingCellFormatter,
          },
        ],
      },
    ],
  },
] satisfies RiskManagementTableColumnDef;

export const RISK_MANAGEMENT_SUMMARY_ROUNDING = {
  overallRisk: coerceNumberFormat(twoDecimalPlaceRounding),
  majorEventRisk: coerceNumberFormat(twoDecimalPlaceRounding),
  scenarioRisk: coerceNumberFormat(twoDecimalPlaceRounding),
  lofSurface: scienceNotationRounding,
  lofSubsurface: scienceNotationRounding,
  lofNuisance: scienceNotationRounding,
  lofIntervention: scienceNotationRounding,
  cofSurface: coerceNumberFormat(twoDecimalPlaceRounding),
  cofSubsurface: coerceNumberFormat(twoDecimalPlaceRounding),
  cofIntervention: coerceNumberFormat(twoDecimalPlaceRounding),
  directEconomicLossSurface: coerceNumberFormat(twoDecimalPlaceRounding),
  directEconomicLossSubsurface: coerceNumberFormat(twoDecimalPlaceRounding),
  propertyDamage: coerceNumberFormat(twoDecimalPlaceRounding),
  responseCost: coerceNumberFormat(twoDecimalPlaceRounding),
  surfaceRepairCost: coerceNumberFormat(twoDecimalPlaceRounding),
  downholeRepairCost: coerceNumberFormat(twoDecimalPlaceRounding),
  commodityLoss: coerceNumberFormat(twoDecimalPlaceRounding),
  serviceDisruption: coerceNumberFormat(twoDecimalPlaceRounding),
  relocation: coerceNumberFormat(twoDecimalPlaceRounding),
  regulatory: coerceNumberFormat(twoDecimalPlaceRounding),
  environmentalTotal: coerceNumberFormat(twoDecimalPlaceRounding),
  emissions: coerceNumberFormat(twoDecimalPlaceRounding),
  airMonitoring: coerceNumberFormat(twoDecimalPlaceRounding),
  reputation: coerceNumberFormat(twoDecimalPlaceRounding),
  healthAndSafetyTotal: coerceNumberFormat(twoDecimalPlaceRounding),
  interventionCrewOnSite: coerceNumberFormat(twoDecimalPlaceRounding),
  injuryFatality: coerceNumberFormat(twoDecimalPlaceRounding),
  health: coerceNumberFormat(twoDecimalPlaceRounding),
};
